import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/pages/Landing";
import { POLICIES, UPDATED, COMPANY } from "@/lib/legalContent";

export default function LegalPage({ slug: fixed }) {
  const { slug: param } = useParams();
  const slug = fixed || param;
  const policy = POLICIES[slug];
  if (!policy) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <SiteNav />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-14 grid lg:grid-cols-4 gap-10" data-testid={`legal-page-${slug}`}>
        <aside className="lg:sticky lg:top-24 self-start">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 mb-3">Legal</p>
          <nav className="flex lg:flex-col gap-1 flex-wrap">
            {Object.entries(POLICIES).map(([k, p]) => (
              <NavLink key={k} to={`/${k}`} data-testid={`legal-nav-${k}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                {p.title}
              </NavLink>
            ))}
          </nav>
        </aside>
        <article className="lg:col-span-3 max-w-3xl">
          <div data-testid="legal-draft-banner" className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-200 p-4 text-sm mb-8">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <p>Draft for legal review. Items in [square brackets] are still to be confirmed.</p>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="legal-title">{policy.title}</h1>
          <p className="text-sm text-slate-500 mt-3">Last updated {UPDATED} · {COMPANY.name}, company no. {COMPANY.number}</p>
          {policy.sections.map(([h, paras], i) => (
            <section key={h} className="mt-10">
              <h2 className="font-display text-lg font-semibold"><span className="font-mono text-xs text-emerald-600 mr-2">{String(i + 1).padStart(2, "0")}</span>{h}</h2>
              {paras.length > 2 && paras.every((p) => p.length < 260) && i > 0
                ? <ul className="mt-3 space-y-2 list-disc pl-5 text-slate-600 dark:text-slate-300 leading-relaxed">{paras.map((p) => <li key={p}>{p}</li>)}</ul>
                : paras.map((p) => <p key={p} className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">{p}</p>)}
            </section>
          ))}
          <p className="mt-12 text-sm text-slate-500">Questions? Email <a href={`mailto:${COMPANY.email}`} className="underline hover:text-emerald-600">{COMPANY.email}</a> or write to {COMPANY.address}. <Link to="/" className="underline hover:text-emerald-600">Back to home</Link></p>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}
